import React, { useEffect, useMemo, useState } from 'react'
import { useOrgSubscriptionItems } from 'hooks/use-org-subscription-items'
import {
  useAdminStripeAvailableProducts,
  useAdminStripeAvailablePrices,
} from 'hooks/use-admin-stripe'
import { StripeProduct } from 'interfaces/stripe-product'
import { Button, Form } from 'react-bootstrap'
import Select from 'react-select'
import ProrationSettings from './proration-settings'
import CouponManager from './coupon-manager'
import { STRIPE_PRIMARY, STRIPE_TEXT_PRIMARY } from 'constants/stripe'
import { sharedStyles } from './shared-styles'
import { formatBillingInterval } from 'utils/billing-interval'
import { formatAmount } from 'utils/organization-utils'

interface CurrentSubscriptionItemsProps {
  orgId: string
  onSubscriptionItemsChange?: (items: any[]) => void
  onResetBillingIntervalChange?: (reset: boolean) => void
  onProrationSettingsChange?: (settings: {
    enabled: boolean
    behavior: 'next_invoice' | 'immediately' | 'none'
  }) => void
  onCouponsChange?: (coupons: any[]) => void
  trialDays?: number | null
}

const CurrentSubscriptionItems: React.FC<CurrentSubscriptionItemsProps> = ({
  orgId,
  onSubscriptionItemsChange,
  onResetBillingIntervalChange,
  onProrationSettingsChange,
  onCouponsChange,
  trialDays,
}) => {
  const styles = {
    container: { marginBottom: '0px' },
    title: { color: STRIPE_TEXT_PRIMARY, marginBottom: '12px' },
    card: {
      backgroundColor: 'white',
      borderRadius: '4px',
      border: 'none',
      overflow: 'hidden',
    },
    emptyState: {
      padding: '16px',
      textAlign: 'center' as const,
      color: '#6b7c93',
    },
    header: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr',
      gap: '8px',
      padding: '8px 12px',
      borderBottom: '1px solid #e6ebf1',
      fontWeight: '600',
      fontSize: '12px',
      color: STRIPE_TEXT_PRIMARY,
      backgroundColor: '#f8f9fa',
    },
    headerCell: { textAlign: 'center' as const },
    headerCellRight: { textAlign: 'right' as const },
    itemRow: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr',
      gap: '8px',
      padding: '12px',
      alignItems: 'center',
      backgroundColor: '#ffffff',
      transition: 'opacity 0.3s ease',
    },
    dimmedItem: {
      opacity: 0.5,
    },
    restoreButton: {
      background: 'none',
      border: '1px solid #635bff',
      color: '#635bff',
      cursor: 'pointer',
      padding: '4px 8px',
      fontSize: '12px',
      borderRadius: '4px',
      marginRight: '8px',
    },

    productName: {
      fontWeight: '600',
      color: STRIPE_TEXT_PRIMARY,
      fontSize: '14px',
      marginBottom: '2px',
    },
    productDetails: { color: '#6b7c93', fontSize: '12px' },
    intervalText: { marginLeft: '8px', color: '#8b9cb3' },
    quantityInput: {
      width: '50px',
      padding: '4px 6px',
      border: '1px solid #e6ebf1',
      borderRadius: '4px',
      textAlign: 'center' as const,
      fontSize: '12px',
    },
    totalAmount: {
      color: STRIPE_TEXT_PRIMARY,
      fontWeight: '500',
      textAlign: 'right' as const,
    },
    removeButton: {
      background: 'none',
      border: 'none',
      color: '#e53d3d',
      cursor: 'pointer',
      padding: '2px',
      fontSize: '14px',
    },
    addButton: {
      background: 'none',
      border: 'none',
      color: STRIPE_PRIMARY,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
    },
    formContainer: {
      marginTop: '16px',
      padding: '24px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e6ebf1',
    },
    formGroup: { marginBottom: '16px' },
    formLabel: { color: STRIPE_TEXT_PRIMARY, fontWeight: '600' },
    buttonGroup: { display: 'flex', gap: '12px' },
    billingSection: {
      marginTop: '24px',
      padding: '16px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
    },
    billingTitle: { color: STRIPE_TEXT_PRIMARY, marginBottom: '12px' },
    checkboxContainer: { display: 'flex', alignItems: 'center', gap: '8px' },
    checkbox: {
      width: '16px',
      height: '16px',
      border: '1px solid #000000',
      borderRadius: '3px',
      cursor: 'pointer',
    },
    checkboxLabel: {
      color: STRIPE_TEXT_PRIMARY,
      cursor: 'pointer',
      margin: 0,
    },
    buttonContainer: {
      marginTop: '12px',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
  }

  const [items, setItems] = useState<any[]>([])
  const [removedItems, setRemovedItems] = useState<any[]>([])

  const removedIds = useMemo(
    () => new Set(removedItems.map((i) => i.id)),
    [removedItems]
  )
  const combinedItems = useMemo(
    () => [...items, ...removedItems],
    [items, removedItems]
  )
  const [selectedProduct, setSelectedProduct] = useState<string>('')
  const [selectedPriceId, setSelectedPriceId] = useState<string>('')
  const [quantity, setQuantity] = useState<number>(1)
  const [showAddForm, setShowAddForm] = useState(false)
  const [resetBillingInterval, setResetBillingInterval] =
    useState<boolean>(false)
  const [prorationEnabled, setProrationEnabled] = useState<boolean>(false)
  const [prorationBehavior, setProrationBehavior] = useState<
    'next_invoice' | 'immediately' | 'none'
  >('immediately')
  const [hasChanges, setHasChanges] = useState(false)
  const [coupons, setCoupons] = useState<any[]>([])
  const [extractedCoupons, setExtractedCoupons] = useState<any[]>([])

  const { originalSubscriptionItems, isLoading } = useOrgSubscriptionItems({
    orgId,
  })

  const {
    data: availableProducts = [],
    isLoading: isProductsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useAdminStripeAvailableProducts()

  const selectedProductPrices = useAdminStripeAvailablePrices(selectedProduct)

  const [originalItems, setOriginalItems] = useState<any[]>([])

  const shouldEnableProration = (currentItems: any[], originalItems: any[]) => {
    if (originalItems.length === 0) {
      return false
    }

    const hasChanges =
      currentItems.length !== originalItems.length ||
      currentItems.some((currentItem) => {
        const originalItem = originalItems.find(
          (orig) =>
            orig.product_id === currentItem.product_id &&
            orig.price_id === currentItem.price_id
        )
        return !originalItem || originalItem.quantity !== currentItem.quantity
      })

    return hasChanges
  }

  // Initialize items from existing subscription with price calculations
  useEffect(() => {
    if (originalSubscriptionItems) {
      // Handle both old structure (array) and new structure (object with items property)
      let itemsArray: any[] = []
      const defaultCoupons: any[] = []

      if (Array.isArray(originalSubscriptionItems)) {
        itemsArray = originalSubscriptionItems
      } else if (
        originalSubscriptionItems &&
        typeof originalSubscriptionItems === 'object' &&
        'items' in originalSubscriptionItems
      ) {
        const { items, discounts } = originalSubscriptionItems as any
        if (Array.isArray(items)) {
          itemsArray = items
        }
        // Extract default coupon from discounts if available
        if (Array.isArray(discounts) && discounts.length > 0) {
          discounts.reduce((acc: any[], d: any) => {
            if (d?.coupon) {
              acc.push({
                id: d.coupon.id,
                name: d.coupon.name,
                percent_off: d.coupon.percent_off,
                amount_off: d.coupon.amount_off,
                currency: d.coupon.currency,
                duration: d.coupon.duration,
                duration_in_months: d.coupon.duration_in_months,
                valid: d.coupon.valid,
                created: d.coupon.created,
                couponDetails: d.coupon,
              })
            }
            return acc
          }, defaultCoupons)
        }
      }

      if (itemsArray.length > 0) {
        // Consolidate duplicate prices by combining quantities
        const consolidatedItems = itemsArray.reduce((acc: any[], item: any) => {
          const existingItem = acc.find(
            (existing: any) => existing.price_id === item.price_id
          )

          if (existingItem) {
            // Combine quantities for the same price
            existingItem.quantity += item.quantity
            return acc
          }
          // Add new item
          acc.push({ ...item })
          return acc
        }, [] as any[])

        const processedItems = consolidatedItems.map((item: any) => {
          // Use the price object from the API response
          const priceData = item.price

          // Calculate unit price - handle tiered pricing and metadata fallback
          let unitPrice = 0
          if (priceData) {
            if (priceData.unit_amount !== null) {
              // Standard pricing
              unitPrice = priceData.unit_amount / 100
            } else if (priceData.metadata?.internal_description) {
              // Extract amount from metadata for tiered pricing
              const metadataMatch =
                priceData.metadata.internal_description.match(
                  /\$(\d+(?:\.\d{2})?)/
                )
              if (metadataMatch) {
                unitPrice = parseFloat(metadataMatch[1])
              }
            }
          }

          const totalAmount = unitPrice * item.quantity

          return {
            ...item,
            id: `${item.product_id}-${item.price_id}`,
            unitPrice,
            totalAmount,
            priceDetails: priceData,
          }
        })

        setItems(processedItems)
        setOriginalItems(processedItems) // Store original for comparison
      }

      // Set default coupons if found
      if (defaultCoupons.length > 0) {
        setExtractedCoupons(defaultCoupons)
      }
    }
  }, [originalSubscriptionItems])

  // Check if proration should be enabled when items change
  useEffect(() => {
    const shouldEnable = shouldEnableProration(items, originalItems)
    setProrationEnabled(shouldEnable)

    // If enabling proration, set behavior to immediately by default
    if (shouldEnable && prorationBehavior === 'none') {
      setProrationBehavior('immediately')
    }
  }, [items, originalItems, prorationBehavior])

  // Update parent component with proration settings
  useEffect(() => {
    // Only enable proration if there are changes
    const finalEnabled = hasChanges ? prorationEnabled : false
    onProrationSettingsChange?.({
      enabled: finalEnabled,
      behavior: prorationBehavior,
    })
  }, [
    prorationEnabled,
    prorationBehavior,
    onProrationSettingsChange,
    hasChanges,
  ])

  // Fetch prices for products that don't have price data
  useEffect(() => {
    if (items.length > 0) {
      const itemsWithoutPrices = items.filter(
        (item) => !item.priceDetails && item.price_id
      )
    }
  }, [items])

  // Track if there are changes
  useEffect(() => {
    const changes =
      items.length !== originalItems.length ||
      items.some((item) => {
        const originalItem = originalItems.find(
          (orig) =>
            orig.product_id === item.product_id &&
            orig.price_id === item.price_id
        )
        return !originalItem || originalItem.quantity !== item.quantity
      })

    setHasChanges(changes)
  }, [items, originalItems])

  useEffect(() => {
    const activeItems = items.filter((item) => item.quantity > 0)
    onSubscriptionItemsChange?.(activeItems)
  }, [items, removedItems, onSubscriptionItemsChange])

  useEffect(() => {
    onResetBillingIntervalChange?.(resetBillingInterval)
  }, [resetBillingInterval, onResetBillingIntervalChange])

  const handleCouponsChange = (newCoupons: any[]) => {
    setCoupons(newCoupons)
    onCouponsChange?.(newCoupons)
  }

  const isNewItem = (item: any) => {
    return !originalItems.some(
      (originalItem) =>
        originalItem.product_id === item.product_id &&
        originalItem.price_id === item.price_id
    )
  }

  // Helper function to get billing interval from price details
  const getBillingInterval = (item: any) => {
    return formatBillingInterval(item)
  }

  // Product options for dropdown
  const productOptions = availableProducts.map((product: StripeProduct) => {
    // Use just the clean product name, not the long label
    const label = product.name

    return {
      value: product.id,
      label,
      // Store additional info for tooltip or detailed display
      metadata: {
        description: product.description,
        internal_description: product.metadata?.internal_description,
        type: product.type,
        active: product.active,
      },
    }
  })

  // Price options for selected product
  const priceOptions = (selectedProductPrices.data || []).map((price) => {
    // Handle tiered pricing where unit_amount might be null
    let priceDisplay = ''
    if (price.unit_amount !== null) {
      priceDisplay = formatAmount(price.unit_amount)
    } else if (price.metadata?.internal_description) {
      // Extract amount from metadata for tiered pricing
      const metadataMatch =
        price.metadata.internal_description.match(/\$(\d+(?:\.\d{2})?)/)
      if (metadataMatch) {
        priceDisplay = `$${metadataMatch[1]}`
      }
    } else {
      priceDisplay = 'Price not available'
    }

    const intervalDisplay =
      price.type === 'recurring' && price.recurring
        ? `/ ${formatBillingInterval({ priceDetails: price })}`
        : '(one-time)'

    let label = `${priceDisplay} ${intervalDisplay}`

    if (
      price.metadata?.description &&
      price.metadata.description !== price.metadata?.internal_description
    ) {
      label += ` - ${price.metadata.description}`
    }

    if (price.billing_scheme === 'tiered' && price.tiers_mode) {
      label += ` - ${price.tiers_mode} tiers`
    }

    // Add currency information
    if (price.currency && price.currency !== 'usd') {
      label += ` (${price.currency.toUpperCase()})`
    }

    return {
      value: price.id,
      label,
      leftSide: label,
      rightSide: price.nickname || '',
      // Store additional info for tooltip or detailed display
      metadata: {
        billing_scheme: price.billing_scheme,
        usage_type: price.recurring?.usage_type,
        nickname: price.nickname,
        description: price.metadata?.description,
        internal_description: price.metadata?.internal_description,
        unit_amount: price.unit_amount,
        type: price.type,
        recurring: price.recurring,
        currency: price.currency,
        tiers_mode: price.tiers_mode,
        created: price.created,
      },
    }
  })

  // Custom option renderer for left-right layout
  const formatPriceOptionLabel = (option: any) => {
    if (!option.rightSide) {
      return option.leftSide
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <span>{option.leftSide}</span>
        <span
          style={{
            fontSize: '12px',
            fontWeight: '500',
          }}
        >
          {option.rightSide}
        </span>
      </div>
    )
  }

  const handleAddProduct = () => {
    if (!selectedProduct || !selectedPriceId) return

    const product = availableProducts.find((p) => p.id === selectedProduct)
    const price = selectedProductPrices.data?.find(
      (p) => p.id === selectedPriceId
    )

    if (!product || !price) return

    // Calculate unit price - handle tiered pricing
    let unitPrice = 0
    if (price.unit_amount !== null) {
      unitPrice = price.unit_amount / 100
    } else if (price.metadata?.internal_description) {
      // Extract amount from metadata for tiered pricing
      const metadataMatch =
        price.metadata.internal_description.match(/\$(\d+(?:\.\d{2})?)/)
      if (metadataMatch) {
        unitPrice = parseFloat(metadataMatch[1])
      }
    }

    const existingItemIndex = items.findIndex(
      (item) => item.price_id === selectedPriceId
    )

    if (existingItemIndex !== -1) {
      setItems((prev) =>
        prev.map((item, index) =>
          index === existingItemIndex
            ? {
                ...item,
                quantity: item.quantity + quantity,
                totalAmount: (item.unitPrice || 0) * (item.quantity + quantity),
              }
            : item
        )
      )
    } else {
      const newItem = {
        id: `${selectedProduct}-${selectedPriceId}`,
        product_id: selectedProduct,
        price_id: selectedPriceId,
        product_name: product.name,
        quantity: quantity,
        unitPrice,
        totalAmount: unitPrice * quantity,
        status: 'active',
        priceDetails: price,
        isNewItem: true,
      }

      setItems((prev) => [...prev, newItem])
    }

    setSelectedProduct('')
    setSelectedPriceId('')
    setQuantity(1)
    setShowAddForm(false)
  }

  const updateQuantity = (itemId: string, newQuantity: number) => {
    setItems(
      (prev) =>
        prev
          .map((item) => {
            if (item.id === itemId) {
              return {
                ...item,
                quantity: newQuantity,
                totalAmount: (item.unitPrice || 0) * newQuantity,
              }
            }
            return item
          })
          .filter((item) => !(item.isNewItem && item.quantity === 0)) // Remove new items with 0 quantity
    )
  }

  const removeItem = (itemId: string) => {
    setItems((prev) => {
      const itemToRemove = prev.find((item) => item.id === itemId)
      if (!itemToRemove) return prev

      // Check if this item existed in the original subscription
      const wasOriginalItem = originalItems.some(
        (originalItem) =>
          originalItem.product_id === itemToRemove.product_id &&
          originalItem.price_id === itemToRemove.price_id
      )

      if (wasOriginalItem) {
        // If it was an original item, move it to removedItems list (preserve it)
        setRemovedItems((removed) => [
          ...removed,
          { ...itemToRemove, removedAt: Date.now() },
        ])
      }

      // Remove from active items (both original and new items get removed from active list)
      return prev.filter((item) => item.id !== itemId)
    })
  }

  const restoreItem = (itemId: string) => {
    setRemovedItems((prev) => {
      const itemToRestore = prev.find((item) => item.id === itemId)
      if (!itemToRestore) return prev

      // Move item back to active items
      setItems((active) => [
        ...active,
        { ...itemToRestore, removedAt: undefined },
      ])

      // Remove from removed items
      return prev.filter((item) => item.id !== itemId)
    })
  }

  if (isLoading) {
    return <div style={{ color: STRIPE_TEXT_PRIMARY }}>Loading...</div>
  }

  return (
    <>
      <h6
        style={{
          ...styles.title,
          fontWeight: 'bold',
          fontSize: '16px',
          color: '#000000',
        }}
      >
        Price
      </h6>

      <div style={styles.container}>
        {/* Items Table */}
        <div style={styles.card}>
          {combinedItems.length > 0 ? (
            <>
              {/* Header */}
              <div style={styles.header}>
                <div>PRODUCT</div>
                <div style={styles.headerCell}>QTY</div>
                <div style={styles.headerCellRight}>TOTAL</div>
                <div style={styles.headerCell} />
              </div>

              {/* Unified Items (active + removed dimmed) */}
              {combinedItems.map((item, index) => {
                const isRemoved = removedIds.has(item.id)
                return (
                  <div
                    key={item.id}
                    style={{
                      ...styles.itemRow,
                      borderBottom:
                        index < combinedItems.length - 1
                          ? '1px solid #f1f5f9'
                          : 'none',
                      ...(isRemoved ? styles.dimmedItem : {}),
                      backgroundColor: isRemoved ? '#f8f9fa' : '#ffffff',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          ...styles.productName,
                          ...(isRemoved ? { opacity: 0.7 } : {}),
                        }}
                      >
                        {item.product_name}
                        {item.priceDetails?.nickname && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '12px',
                              fontWeight: '400',
                            }}
                          >
                            - {item.priceDetails.nickname}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          ...styles.productDetails,
                          ...(isRemoved ? { opacity: 0.7 } : {}),
                        }}
                      >
                        {formatAmount((item.unitPrice || 0) * 100)} USD
                        {getBillingInterval(item) && (
                          <span style={styles.intervalText}>
                            / {getBillingInterval(item)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        ...(isRemoved ? { opacity: 0.7 } : {}),
                      }}
                    >
                      <input
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = parseInt(e.target.value, 10)
                          updateQuantity(item.id, Number.isNaN(val) ? 0 : val)
                        }}
                        disabled={isRemoved}
                        style={styles.quantityInput}
                      />
                    </div>

                    <div
                      style={{
                        ...styles.totalAmount,
                        ...(isRemoved ? { opacity: 0.7 } : {}),
                      }}
                    >
                      {formatAmount((item.totalAmount || 0) * 100)}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {isRemoved ? (
                        <button
                          type="button"
                          onClick={() => restoreItem(item.id)}
                          style={styles.restoreButton}
                          title="Restore item"
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          style={{
                            ...styles.removeButton,
                            width: '28px',
                            height: '28px',
                            backgroundColor: 'white',
                            border: '1px solid #e6ebf1',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                          }}
                          title="Remove item"
                        >
                          <span
                            style={{
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: '#e53d3d',
                            }}
                          >
                            ×
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </>
          ) : (
            <div style={styles.emptyState}>No current items</div>
          )}
        </div>

        {/* Add Product and Coupon Buttons - Two Sections Layout */}
        <div style={styles.buttonContainer}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            {/* Section 1: Add Product Button - Left Side */}
            <div style={{ flex: '0 0 auto' }}>
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                style={styles.addButton}
              >
                + Add product
              </button>
            </div>

            {/* Section 2: Coupon Manager Component - Right Side (Half Width) */}
            <div
              style={{
                flex: '0 0 50%',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <CouponManager
                onCouponsChange={handleCouponsChange}
                initialCoupons={extractedCoupons}
              />
            </div>
          </div>
        </div>

        {/* Add Product Form */}
        {showAddForm && (
          <div style={styles.formContainer}>
            <div style={styles.formGroup}>
              <Form.Label style={styles.formLabel}>Select Product</Form.Label>
              <Select
                options={productOptions}
                value={productOptions.find(
                  (option) => option.value === selectedProduct
                )}
                onChange={(option: any) => {
                  setSelectedProduct(option?.value || '')
                  setSelectedPriceId('')
                }}
                placeholder="Choose a product..."
                isClearable
                styles={{
                  control: (base: any) => ({
                    ...base,
                    backgroundColor: '#ffffff',
                    borderColor: '#e6ebf1',
                    minHeight: '38px',
                    '&:hover': {
                      borderColor: '#635bff',
                    },
                  }),
                  menu: (base: any) => ({
                    ...base,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e6ebf1',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxHeight: '300px',
                  }),
                  option: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? '#635bff'
                      : state.isFocused
                      ? '#f8f9fa'
                      : '#ffffff',
                    color: state.isSelected ? '#ffffff' : '#32325d',
                    padding: '8px 12px',
                    fontSize: '13px',
                    lineHeight: '1.4',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    '&:hover': {
                      backgroundColor: state.isSelected ? '#635bff' : '#f8f9fa',
                    },
                  }),
                  singleValue: (base: any) => ({
                    ...base,
                    color: '#32325d',
                    fontSize: '13px',
                    lineHeight: '1.4',
                  }),
                  placeholder: (base: any) => ({
                    ...base,
                    color: '#6b7c93',
                    fontSize: '13px',
                  }),
                  input: (base: any) => ({
                    ...base,
                    color: '#32325d',
                    fontSize: '13px',
                  }),
                }}
              />
            </div>

            {selectedProduct && (
              <div style={styles.formGroup}>
                <Form.Label style={styles.formLabel}>Select Price</Form.Label>
                <Select
                  options={priceOptions}
                  value={priceOptions.find(
                    (option) => option.value === selectedPriceId
                  )}
                  onChange={(option: any) =>
                    setSelectedPriceId(option?.value || '')
                  }
                  placeholder="Choose a price..."
                  isClearable
                  formatOptionLabel={formatPriceOptionLabel}
                  styles={{
                    control: (base: any) => ({
                      ...base,
                      backgroundColor: '#ffffff',
                      borderColor: '#e6ebf1',
                      minHeight: '38px',
                      '&:hover': {
                        borderColor: '#635bff',
                      },
                    }),
                    menu: (base: any) => ({
                      ...base,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e6ebf1',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      maxHeight: '300px',
                    }),
                    option: (base: any, state: any) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? '#635bff'
                        : state.isFocused
                        ? '#f8f9fa'
                        : '#ffffff',
                      color: state.isSelected ? '#ffffff' : '#32325d',
                      padding: '8px 12px',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      '&:hover': {
                        backgroundColor: state.isSelected
                          ? '#635bff'
                          : '#f8f9fa',
                      },
                    }),
                    singleValue: (base: any) => ({
                      ...base,
                      color: '#32325d',
                      fontSize: '13px',
                      lineHeight: '1.4',
                    }),
                    placeholder: (base: any) => ({
                      ...base,
                      color: '#6b7c93',
                      fontSize: '13px',
                    }),
                    input: (base: any) => ({
                      ...base,
                      color: '#32325d',
                      fontSize: '13px',
                    }),
                  }}
                />
              </div>
            )}

            {selectedPriceId && (
              <div style={styles.formGroup}>
                <Form.Label style={styles.formLabel}>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = parseInt(e.target.value, 10)
                    setQuantity(Number.isNaN(val) ? 0 : val)
                  }}
                  style={{ width: '100px' }}
                />
              </div>
            )}

            <div style={styles.buttonGroup}>
              <Button
                onClick={handleAddProduct}
                disabled={!selectedProduct || !selectedPriceId}
                style={{
                  backgroundColor: STRIPE_PRIMARY,
                  borderColor: STRIPE_PRIMARY,
                }}
              >
                Add Product
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowAddForm(false)
                  setSelectedProduct('')
                  setSelectedPriceId('')
                  setQuantity(1)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Billing Cycle Details Section */}
        <div style={sharedStyles.sectionContainer}>
          <h6 style={sharedStyles.sectionHeading}>Billing cycle details</h6>
          <div style={sharedStyles.checkboxContainer}>
            <input
              type="checkbox"
              id="resetBillingInterval"
              checked={resetBillingInterval}
              onChange={(e) => setResetBillingInterval(e.target.checked)}
              style={{
                ...sharedStyles.checkbox,
                opacity: (trialDays || 0) > 0 ? 0.5 : 1,
                cursor: (trialDays || 0) > 0 ? 'not-allowed' : 'pointer',
              }}
              disabled={Boolean((trialDays || 0) > 0)}
            />
            <Button
              variant="outline-secondary"
              onClick={() => setResetBillingInterval(!resetBillingInterval)}
              disabled={Boolean((trialDays || 0) > 0)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: (trialDays || 0) > 0 ? '#6b7c93' : STRIPE_TEXT_PRIMARY,
                padding: '6px 12px',
                fontSize: '14px',
                cursor: (trialDays || 0) > 0 ? 'not-allowed' : 'pointer',
                opacity: (trialDays || 0) > 0 ? 0.5 : 1,
              }}
            >
              Reset Billing Interval
            </Button>
          </div>
        </div>

        {/* Proration Settings Component */}
        <ProrationSettings
          onProrationChange={(enabled, behavior) => {
            onProrationSettingsChange?.({ enabled, behavior })
          }}
          initialEnabled={prorationEnabled}
          initialBehavior={prorationBehavior}
          hasChanges={hasChanges}
        />
      </div>
    </>
  )
}

export default CurrentSubscriptionItems
